import { Answer } from "@edu/shared";

/**
 * Черновики ответов домашки в localStorage (9.1 docs/homework-design.md).
 *
 * > Черновик живёт в `localStorage`, ключ `hw:<homeworkId>:<elementId>:<userId>`;
 * > сервер узнаёт об ответе только в момент «Проверить», попытка и есть
 * > серверная запись.
 *
 * Y.Doc здесь не заводится намеренно: у одиночного редактирования нет второго
 * участника, а документ «нарисовал, стёр» без естественной границы ротации это
 * дорога к yjs#741.
 *
 * ⚠️ `userId` в ключе обязателен: за одним телефоном могут смениться два
 * гостя, и черновик одного не должен всплыть у другого.
 *
 * ⚠️ Каждый `setItem` в try/catch, и отказ квоты это ВИДИМОЕ состояние: Safari
 * в приватном режиме бросает QuotaExceededError на первой же записи, то есть
 * это штатный браузер ученика, а не экзотика. Ленту об отказе извещает
 * возвращаемое значение `saveDraft`.
 */

function draftKey(homeworkId: string, elementId: string, userId: string): string {
  return `hw:${homeworkId}:${elementId}:${userId}`;
}

export function loadDraft(homeworkId: string, elementId: string, userId: string): Answer | null {
  try {
    const raw = localStorage.getItem(draftKey(homeworkId, elementId, userId));
    if (raw === null) return null;
    const parsed = Answer.safeParse(JSON.parse(raw));
    // Мягко: черновик старого бандла, который не разобрался, это «черновика
    // нет», а не сломанная лента
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** `false` означает «хранилище не пишет»: черновик остаётся в памяти вкладки. */
export function saveDraft(
  homeworkId: string,
  elementId: string,
  userId: string,
  answer: Answer,
): boolean {
  try {
    localStorage.setItem(draftKey(homeworkId, elementId, userId), JSON.stringify(answer));
    return true;
  } catch {
    return false;
  }
}

export function removeDraft(homeworkId: string, elementId: string, userId: string): void {
  try {
    localStorage.removeItem(draftKey(homeworkId, elementId, userId));
  } catch {
    /* хранилище не пишет: удалять тоже нечего */
  }
}

/**
 * Черновик ответа текстом ко всей домашке (1.8 docs/homework-scratch-design.md).
 * Ключ `hw:<homeworkId>:answer:<userId>`: начинается с домашки и кончается
 * пользователем, поэтому `cleanupDrafts` снимает его тем же правилом, что
 * черновики заданий. С `elementId` он не столкнётся: у заданий это uuid или
 * `ex-…`, а не слово `answer`.
 */
function answerKey(homeworkId: string, userId: string): string {
  return `hw:${homeworkId}:answer:${userId}`;
}

/**
 * Черновик ответа вместе с моментом последней правки на этом устройстве.
 * Момент нужен, чтобы черновик, оставленный на компьютере, не перебил ответ,
 * отправленный позже с телефона (`initialAnswerDraft`).
 */
export interface AnswerDraft {
  text: string;
  /** Часы устройства, миллисекунды эпохи. */
  at: number;
}

export function loadAnswerDraft(homeworkId: string, userId: string): AnswerDraft | null {
  try {
    const raw = localStorage.getItem(answerKey(homeworkId, userId));
    return raw === null ? null : parseAnswerDraft(raw);
  } catch {
    return null;
  }
}

/**
 * Разбор записи. Мягко: запись без момента (так писал бандл первого дня) или
 * кривая это «черновика нет», а не сломанное поле. Без момента нельзя понять,
 * старше ли он отправленного, а угадывать в пользу черновика значит рисковать
 * свежим ответом.
 */
export function parseAnswerDraft(raw: string): AnswerDraft | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { text, at } = value as { text?: unknown; at?: unknown };
    return typeof text === "string" && typeof at === "number" && Number.isFinite(at)
      ? { text, at }
      : null;
  } catch {
    return null;
  }
}

/** `false` означает «хранилище не пишет»: черновик остаётся в памяти вкладки. */
export function saveAnswerDraft(homeworkId: string, userId: string, draft: AnswerDraft): boolean {
  try {
    localStorage.setItem(answerKey(homeworkId, userId), JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function removeAnswerDraft(homeworkId: string, userId: string): void {
  try {
    localStorage.removeItem(answerKey(homeworkId, userId));
  } catch {
    /* хранилище не пишет: удалять тоже нечего */
  }
}

/**
 * Какой возврат проверки перевода уже разобран на этом устройстве: время
 * возврата (2.10 docs/translate-review-design.md). По нему лента заполняет
 * поля исправлений текстом проверенной попытки ровно один раз, а не на каждой
 * перезагрузке, стирая начатое.
 *
 * Ключ начинается с `hw:<homeworkId>:` и кончается `userId`: уборка ниже
 * снимает его вместе с черновиками той же домашки, отдельного правила ей не
 * нужно.
 */
function seedKey(homeworkId: string, elementId: string, userId: string): string {
  return `hw:${homeworkId}:${elementId}:review:${userId}`;
}

export function loadReviewSeed(homeworkId: string, elementId: string, userId: string): number | null {
  try {
    const raw = localStorage.getItem(seedKey(homeworkId, elementId, userId));
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

export function saveReviewSeed(
  homeworkId: string,
  elementId: string,
  userId: string,
  returnedAt: number,
): void {
  try {
    localStorage.setItem(seedKey(homeworkId, elementId, userId), String(returnedAt));
  } catch {
    /* хранилище не пишет: черновик тоже не пережил бы перезагрузку, и заполнить
       поля заново при следующем открытии это ровно то, что нужно */
  }
}

/**
 * Уборка ключей при каждом получении списка домашек (9.1 проекта): удаляются
 * черновики домашек, которых в списке больше нет (удалена, стёрта уборкой), и
 * домашек, которым черновик больше нечего спасать (выполнена и раскрыта).
 *
 * Сервер дотянуться до чужого localStorage не может физически, поэтому уборка
 * привязана к визиту; на устройстве, куда человек не вернулся, черновики живут
 * до чистки браузера, и это названная цена.
 *
 * ⚠️ `complete` появился вместе с курсором (4.3 фазы 2) и защищает от тихой
 * потери ответов. Правило «нет в списке, значит домашки нет» верно только для
 * ПОЛНОГО списка, а страница курсора это двадцать пять свежих работ. Уборка по
 * такой странице стёрла бы черновики всех остальных, причём молча и у ученика,
 * то есть ровно то, чего продукт обещает не делать.
 */
export function cleanupDrafts(
  userId: string,
  args: {
    known: ReadonlySet<string>;
    finished: ReadonlySet<string>;
    /** Список дочитан до конца и не сужен фильтром или разрезом. */
    complete: boolean;
  },
): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null || !key.startsWith("hw:") || !key.endsWith(`:${userId}`)) continue;
      const homeworkId = key.split(":")[1];
      if (homeworkId === undefined) continue;
      const gone = args.complete && !args.known.has(homeworkId);
      if (gone || args.finished.has(homeworkId)) doomed.push(key);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    /* приватный режим: чистить нечего, писать туда тоже не выходило */
  }
}
